import { useCallback, useEffect, useRef } from "react";
import { Platform, ScrollView, View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../../theme";
import type { DropdownSlotProps, ResultState, SearchResult } from "../types";
import { SearchEmpty } from "./SearchEmpty";
import { SearchGroupHeader } from "./SearchGroupHeader";
import { SearchLoading } from "./SearchLoading";
import { SearchResultRow } from "./SearchResultRow";

function SearchDropdown<T extends SearchResult>({
  resultGroups,
  isLoading,
  focusedIndex,
  onResultPress,
  onResultHover,
  renderGroupHeader,
  renderResult,
  renderLoading,
  renderEmpty,
}: DropdownSlotProps<T>) {
  const scrollRef = useRef<ScrollView>(null);
  const itemRefs = useRef<Map<number, View>>(new Map());
  const groupRefs = useRef<Map<number, View>>(new Map());
  const firstInGroupIndices = useRef<Set<number>>(new Set());

  const setItemRef = useCallback((index: number, ref: View | null) => {
    if (ref) {
      itemRefs.current.set(index, ref);
    } else {
      itemRefs.current.delete(index);
    }
  }, []);

  const setGroupRef = useCallback((firstIndex: number, ref: View | null) => {
    if (ref) {
      groupRefs.current.set(firstIndex, ref);
    } else {
      groupRefs.current.delete(firstIndex);
    }
  }, []);

  useEffect(() => {
    if (focusedIndex < 0) return;

    const isFirstInGroup = firstInGroupIndices.current.has(focusedIndex);
    const targetRef = isFirstInGroup ? groupRefs.current.get(focusedIndex) : itemRefs.current.get(focusedIndex);
    const scrollView = scrollRef.current;
    if (!targetRef || !scrollView) return;

    if (Platform.OS === "web") {
      const node = targetRef as unknown as HTMLElement;
      node.scrollIntoView({ block: "nearest" });
    } else {
      targetRef.measureLayout(
        scrollView.getInnerViewNode() as number,
        (_x, y, _w, h) => {
          scrollRef.current?.scrollTo({ y: y - h, animated: true });
        },
        () => {},
      );
    }
  }, [focusedIndex, resultGroups, isLoading]);

  let globalIndex = 0;
  firstInGroupIndices.current.clear();

  if (isLoading) {
    return <View style={styles.dropdown}>{renderLoading ? renderLoading() : <SearchLoading />}</View>;
  }

  const hasResults = resultGroups.some((g) => g.results.length > 0);
  if (!hasResults) {
    return <View style={styles.dropdown}>{renderEmpty ? renderEmpty() : <SearchEmpty />}</View>;
  }

  return (
    <View style={styles.dropdown}>
      <ScrollView ref={scrollRef} style={styles.dropdownScroll} keyboardShouldPersistTaps="handled">
        {resultGroups.map((group) => {
          if (group.results.length === 0) return null;
          const groupFirstIndex = globalIndex;
          if (group.name.length > 0) {
            firstInGroupIndices.current.add(groupFirstIndex);
          }
          return (
            <View key={group.id} ref={(ref) => setGroupRef(groupFirstIndex, ref)}>
              {group.name.length > 0 &&
                (renderGroupHeader ? renderGroupHeader(group) : <SearchGroupHeader group={group} />)}
              {group.results.map((result) => {
                const currentIndex = globalIndex;
                globalIndex += 1;
                const state: ResultState = { focused: currentIndex === focusedIndex, index: currentIndex };
                if (renderResult) {
                  return (
                    <View key={result.id} ref={(ref) => setItemRef(currentIndex, ref)}>
                      {renderResult(result, state)}
                    </View>
                  );
                }
                return (
                  <View key={result.id} ref={(ref) => setItemRef(currentIndex, ref)}>
                    <SearchResultRow
                      result={result}
                      state={state}
                      onPress={() => onResultPress(result)}
                      onHoverIn={() => onResultHover(currentIndex)}
                    />
                  </View>
                );
              })}
            </View>
          );
        })}
      </ScrollView>
    </View>
  );
}

SearchDropdown.displayName = "SearchDropdown";

export { SearchDropdown };

const styles = StyleSheet.create((theme) => ({
  dropdown: {
    position: "absolute",
    top: 48,
    left: 0,
    right: 0,
    maxHeight: 300,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.spacing.xl,
    ...theme.shadows.sm,
    zIndex: 20,
    overflow: "hidden",
  },
  dropdownScroll: {
    maxHeight: 300,
  },
}));
